import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Camera, Upload, Sparkles, Download, Share2, ShoppingCart, 
  X, Loader2, Check, Instagram, Facebook, Linkedin, Twitter
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useCart } from '../context/CartContext';

const SCENARIOS = [
  { 
    id: 'fitting_room', 
    label: 'In the Fitting Room', 
    description: 'See yourself considering the purchase',
    icon: '🪞'
  },
  { 
    id: 'giving_speech', 
    label: 'Giving a Speech', 
    description: 'Command the stage with confidence',
    icon: '🎤'
  },
  { 
    id: 'nightclub', 
    label: 'At the Nightclub', 
    description: 'VIP section ready',
    icon: '🍾'
  },
  { 
    id: 'restaurant', 
    label: 'At a Nice Restaurant', 
    description: 'Elegant dining vibes',
    icon: '🍽️'
  }
];

// PRSVR Motivational Quotes based on fundamentals
const PRSVR_QUOTES = [
  { word: 'PASSION', quote: "Your fire is your fuel. Let it burn bright." },
  { word: 'PASSION', quote: "Chase what sets your soul on fire." },
  { word: 'RESILIENCE', quote: "Fall seven times, stand up eight." },
  { word: 'RESILIENCE', quote: "The comeback is always stronger than the setback." },
  { word: 'RESILIENCE', quote: "Pressure creates diamonds." },
  { word: 'SACRIFICE', quote: "Discipline is choosing between what you want now and what you want most." },
  { word: 'SACRIFICE', quote: "The price of excellence is discipline. The cost of mediocrity is disappointment." },
  { word: 'VALUES', quote: "Stand for something or fall for anything." },
  { word: 'VALUES', quote: "Integrity is doing the right thing, even when no one is watching." },
  { word: 'RESPECT', quote: "How you treat others defines who you are." },
  { word: 'RESPECT', quote: "Earn your reputation. Protect it like gold." },
  { word: 'PRSVR', quote: "Persevere. The vision is worth the struggle." },
  { word: 'PRSVR', quote: "Stay locked in. The mission is the moment." },
  { word: 'PRSVR', quote: "Tunnel Vision: Where distractions fade and only the mission remains." }
];

const VirtualTryOn = ({ product, isOpen, onClose }) => {
  const { addToCart } = useCart();
  const [step, setStep] = useState('upload'); // upload, scenarios, generating, results
  const [userImage, setUserImage] = useState(null);
  const [userImagePreview, setUserImagePreview] = useState(null);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [currentQuote, setCurrentQuote] = useState(PRSVR_QUOTES[0]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [showSizeSelector, setShowSizeSelector] = useState(false);
  const fileInputRef = useRef(null);

  // Rotate quotes during generation
  useEffect(() => {
    let quoteInterval;
    if (isGenerating) {
      quoteInterval = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * PRSVR_QUOTES.length);
        setCurrentQuote(PRSVR_QUOTES[randomIndex]);
      }, 3500);
    }
    return () => clearInterval(quoteInterval);
  }, [isGenerating]);

  // Get available sizes from product variants
  const availableSizes = product?.variants?.filter(v => v.available)?.map(v => ({
    id: v.id,
    title: v.title,
    available: v.available
  })) || [];

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setUserImagePreview(e.target.result);
      setUserImage(e.target.result); // Base64 string
      setStep('scenarios');
    };
    reader.readAsDataURL(file);
  }, []);

  const toggleScenario = (scenarioId) => {
    setSelectedScenarios(prev => 
      prev.includes(scenarioId)
        ? prev.filter(s => s !== scenarioId)
        : [...prev, scenarioId]
    );
  };

  const selectAllScenarios = () => {
    setSelectedScenarios(SCENARIOS.map(s => s.id));
  };

  const handleGenerate = async () => {
    if (selectedScenarios.length === 0) {
      toast.error('Please select at least one scenario');
      return;
    }

    setIsGenerating(true);
    setStep('generating');
    setGenerationProgress(0);
    setCurrentQuote(PRSVR_QUOTES[Math.floor(Math.random() * PRSVR_QUOTES.length)]);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => Math.min(prev + 5, 90));
      }, 1500);

      const response = await api.post('/virtual-try-on/generate', {
        user_image_base64: userImage,
        product_name: product?.name || product?.title || 'Product',
        product_image_url: product?.images?.[0]?.url || product?.image?.url || '',
        scenarios: selectedScenarios
      });

      clearInterval(progressInterval);
      setGenerationProgress(100);

      if (response.data.success) {
        setGeneratedImages(response.data.images);
        setStep('results');
        toast.success('Your styled looks are ready!');
      } else {
        throw new Error(response.data.message || 'Generation failed');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate images. Please try again.');
      setStep('scenarios');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = (imageData, scenario) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageData}`;
    link.download = `PRSVR-${product?.name || 'Look'}-${scenario}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded!');
  };

  const shareToSocial = (platform, imageData, scenario) => {
    const productUrl = window.location.href;
    const shareText = encodeURIComponent(
      `I'm thinking about getting this ${product?.name || product?.title || 'look'} from PRSVR. What do you think? ${productUrl}`
    );

    const shareUrls = {
      instagram: `https://www.instagram.com/`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}&quote=${shareText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(productUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${shareText}`,
      tiktok: `https://www.tiktok.com/`
    };

    if (platform === 'instagram' || platform === 'tiktok') {
      downloadImage(imageData, scenario);
      toast.info(`Image downloaded! Open ${platform === 'instagram' ? 'Instagram' : 'TikTok'} and share from your gallery.`);
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleAddToCart = async (variantId = null) => {
    const targetVariantId = variantId || selectedSize;
    
    if (!targetVariantId && availableSizes.length > 1) {
      setShowSizeSelector(true);
      return;
    }
    
    const finalVariantId = targetVariantId || product?.variants?.[0]?.id;
    
    if (finalVariantId) {
      await addToCart(finalVariantId, 1);
      toast.success(`${product?.name || product?.title} added to cart!`);
      setShowSizeSelector(false);
      setSelectedSize(null);
    }
  };

  const resetFlow = () => {
    setStep('upload');
    setUserImage(null);
    setUserImagePreview(null);
    setSelectedScenarios([]);
    setGeneratedImages([]);
    setGenerationProgress(0);
    setSelectedSize(null);
    setShowSizeSelector(false);
  };

  // Size Selector Component
  const SizeSelector = ({ onSelect, onCancel }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4 text-center">Select Your Size</h3>
        <div className="grid grid-cols-3 gap-3 mb-6">
          {availableSizes.map((size) => (
            <button
              key={size.id}
              onClick={() => {
                setSelectedSize(size.id);
                onSelect(size.id);
              }}
              className={`p-3 border-2 rounded-lg font-bold transition-all ${
                selectedSize === size.id
                  ? 'border-warmSage bg-warmSage/20 text-warmSage'
                  : 'border-gray-600 hover:border-gray-400 text-white'
              }`}
            >
              {size.title}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={onCancel}
            className="flex-1 border-gray-600 text-gray-300"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { onClose(); resetFlow(); }}}>
      <DialogContent className="bg-black border border-gray-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-warmSage" />
            Virtual Fitting & Scenario Styling
          </DialogTitle>
        </DialogHeader>

        {/* Size Selector Modal */}
        {showSizeSelector && (
          <SizeSelector 
            onSelect={handleAddToCart}
            onCancel={() => setShowSizeSelector(false)}
          />
        )}

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="py-8">
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold mb-2">Upload Your Photo</h3>
              <p className="text-gray-400">
                Upload a full-body photo to see yourself in the {product?.name || 'product'}
              </p>
            </div>

            <div 
              className="border-2 border-dashed border-gray-700 rounded-lg p-12 text-center cursor-pointer hover:border-warmSage transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-gray-500" />
              <p className="text-lg font-bold mb-2">Click to upload or drag and drop</p>
              <p className="text-sm text-gray-500">PNG, JPG up to 10MB • Full-body photos work best</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />

            <div className="mt-6 p-4 bg-gray-900 rounded-lg">
              <p className="text-sm text-gray-400">
                <span className="text-warmSage font-bold">Privacy Note:</span> Your photo is processed securely and not stored on our servers after your session.
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Select Scenarios */}
        {step === 'scenarios' && (
          <div className="py-6">
            <div className="flex items-start gap-6 mb-8">
              {/* User Image Preview */}
              <div className="w-32 h-40 rounded-lg overflow-hidden border-2 border-warmSage flex-shrink-0">
                <img 
                  src={userImagePreview} 
                  alt="Your photo" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Choose Your Scenarios</h3>
                <p className="text-gray-400 mb-4">
                  Select where you'd like to see yourself wearing the {product?.name || 'product'}
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={selectAllScenarios}
                  className="border-warmSage text-warmSage hover:bg-warmSage hover:text-black"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Select All
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {SCENARIOS.map((scenario) => (
                <div
                  key={scenario.id}
                  onClick={() => toggleScenario(scenario.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedScenarios.includes(scenario.id)
                      ? 'border-warmSage bg-warmSage/10'
                      : 'border-gray-700 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{scenario.icon}</span>
                    <div>
                      <p className="font-bold">{scenario.label}</p>
                      <p className="text-sm text-gray-400">{scenario.description}</p>
                    </div>
                    {selectedScenarios.includes(scenario.id) && (
                      <Check className="w-5 h-5 text-warmSage ml-auto" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Button 
                variant="outline" 
                onClick={() => setStep('upload')}
                className="border-gray-600 text-gray-300"
              >
                Back
              </Button>
              <Button 
                onClick={handleGenerate}
                disabled={selectedScenarios.length === 0}
                className="flex-1 bg-warmSage hover:bg-warmSage/90 text-black font-bold"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate {selectedScenarios.length} Look{selectedScenarios.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Generating with PRSVR Quotes */}
        {step === 'generating' && (
          <div className="py-12 text-center">
            <div className="relative w-24 h-24 mx-auto mb-8">
              <Loader2 className="w-24 h-24 animate-spin text-warmSage" />
              <Sparkles className="w-8 h-8 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white" />
            </div>
            
            <h3 className="text-2xl font-bold mb-2">Creating Your Looks...</h3>
            <p className="text-gray-400 mb-8">
              Our AI is styling you in {selectedScenarios.length} scenario{selectedScenarios.length !== 1 ? 's' : ''}
            </p>
            
            {/* PRSVR Motivational Quote */}
            <div className="max-w-lg mx-auto mb-8 p-6 bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-lg">
              <div className="mb-3">
                <span className="text-warmSage font-black text-sm tracking-[0.3em] uppercase">
                  {currentQuote.word}
                </span>
              </div>
              <p className="text-white text-xl font-bold italic leading-relaxed">
                "{currentQuote.quote}"
              </p>
              <div className="mt-4 flex justify-center gap-1">
                {['P', 'R', 'S', 'V', 'R'].map((letter, i) => (
                  <span 
                    key={i} 
                    className={`text-xs font-black ${
                      currentQuote.word.startsWith(letter) || currentQuote.word === 'PRSVR'
                        ? 'text-warmSage' 
                        : 'text-gray-600'
                    }`}
                  >
                    {letter}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="max-w-md mx-auto">
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-warmSage to-warmSage/70 transition-all duration-500"
                  style={{ width: `${generationProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">{generationProgress}% complete</p>
            </div>

            <p className="text-xs text-gray-600 mt-8">
              Stay locked in. Your vision is loading...
            </p>
          </div>
        )}

        {/* Step 4: Results */}
        {step === 'results' && (
          <div className="py-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-2">Your Styled Looks</h3>
              <p className="text-gray-400">
                Here's how you'd look in the {product?.name || 'product'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {generatedImages.map((img, index) => (
                <div key={index} className="bg-gray-900 rounded-lg overflow-hidden">
                  {img.success ? (
                    <>
                      <div className="aspect-[3/4] relative">
                        <img 
                          src={`data:${img.mime_type || 'image/png'};base64,${img.image_data}`}
                          alt={img.scenario_label}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-3 left-3 bg-black/70 px-3 py-1 rounded-full">
                          <span className="text-sm font-bold">{img.scenario_label}</span>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="p-4 space-y-3">
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => downloadImage(img.image_data, img.scenario)}
                            className="flex-1 border-gray-600 text-white hover:bg-gray-800"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => handleAddToCart()}
                            className="flex-1 bg-warmSage hover:bg-warmSage/90 text-black font-bold"
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Add to Cart
                          </Button>
                        </div>
                        
                        {/* Social Share */}
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-xs text-gray-500 mr-2">Share:</span>
                          <button 
                            onClick={() => shareToSocial('instagram', img.image_data, img.scenario)}
                            className="p-2 rounded-full hover:bg-gray-800 transition-colors"
                            title="Share on Instagram"
                          >
                            <Instagram className="w-4 h-4 text-pink-500" />
                          </button>
                          <button 
                            onClick={() => shareToSocial('facebook', img.image_data, img.scenario)}
                            className="p-2 rounded-full hover:bg-gray-800 transition-colors"
                            title="Share on Facebook"
                          >
                            <Facebook className="w-4 h-4 text-blue-500" />
                          </button>
                          <button 
                            onClick={() => shareToSocial('linkedin', img.image_data, img.scenario)}
                            className="p-2 rounded-full hover:bg-gray-800 transition-colors"
                            title="Share on LinkedIn"
                          >
                            <Linkedin className="w-4 h-4 text-blue-400" />
                          </button>
                          <button 
                            onClick={() => shareToSocial('twitter', img.image_data, img.scenario)}
                            className="p-2 rounded-full hover:bg-gray-800 transition-colors"
                            title="Share on X"
                          >
                            <Twitter className="w-4 h-4 text-white" />
                          </button>
                          <button 
                            onClick={() => shareToSocial('tiktok', img.image_data, img.scenario)}
                            className="p-2 rounded-full hover:bg-gray-800 transition-colors"
                            title="Share on TikTok"
                          >
                            <span className="text-sm">🎵</span>
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="aspect-[3/4] flex items-center justify-center bg-gray-800">
                      <div className="text-center p-4">
                        <X className="w-12 h-12 mx-auto mb-2 text-red-500" />
                        <p className="text-gray-400">{img.scenario_label}</p>
                        <p className="text-sm text-red-400 mt-2">Generation failed</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Button 
                variant="outline" 
                onClick={resetFlow}
                className="border-gray-600 text-gray-300"
              >
                Try Another Photo
              </Button>
              <Button 
                onClick={() => handleAddToCart()}
                className="flex-1 bg-warmSage hover:bg-warmSage/90 text-black font-bold"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Add {product?.name || 'Product'} to Cart
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VirtualTryOn;
